/**
 * GMA-1616 (CompassCare Web 1/14 — Foundation) — reusable severity chip.
 *
 * The single severity indicator used across the facelift (table rows, KPIs,
 * map-pin legends, alert drawers). Color + label come from the one design-token
 * set (src/styles/designTokens) — callers never define per-page severity colors.
 *
 * Accessibility (GMA-1616 AC): the color dot is decorative (aria-hidden); the
 * text label is always rendered and carries the meaning, so severity is never
 * conveyed by color alone.
 */
import React from "react";
import PropTypes from "prop-types";
import { severityToken, SEVERITY_LEVELS } from "../../styles/designTokens";

export default function SeverityBadge({
  level,
  label,
  showDot = true,
  className,
  style,
}) {
  // Normalize the level once so the data-severity hook and the resolved token
  // can never disagree.
  const normalizedLevel = SEVERITY_LEVELS.includes(level) ? level : "normal";
  const token = severityToken(normalizedLevel);
  const text = label || token.label;

  return (
    <span
      data-testid="severity-badge"
      data-severity={normalizedLevel}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        // Caller `style` may tune layout, but the severity color stays
        // token-driven (single source) — token color wins over any spread.
        ...style,
        color: token.color,
      }}
    >
      {showDot && (
        <span
          data-testid="severity-badge-dot"
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: token.color,
            flex: "0 0 auto",
          }}
        />
      )}
      <span>{text}</span>
    </span>
  );
}

SeverityBadge.propTypes = {
  /** One of SEVERITY_LEVELS; unknown/missing degrades to "normal". */
  level: PropTypes.string,
  /** Optional label override (e.g. "Fall detected"); defaults to the token label. */
  label: PropTypes.string,
  /** Hide the decorative color dot for text-only contexts. */
  showDot: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};
